import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Minus, TrendingUp, Type as TypeIcon, Ruler, Undo2, Redo2, Trash2 } from 'lucide-react';

/**
 * DrawingOverlay — Phase 52.B interactive drawing layer that sits on
 * top of the chart canvas. Uses the ChartEngine's provider handles
 * (`chart.timeScale()`, `series.priceToCoordinate()`) to translate
 * between (time, price) and pixel coords so drawings stay anchored to
 * the chart even when the user pans or zooms.
 *
 * Supported tools (52.B):
 *   • horizontal — single-price horizontal line at a click's price
 *   • trend      — trend line between two clicks
 *   • fib        — fibonacci retracement between two clicks (0/23.6/38.2/50/61.8/78.6/100)
 *   • text       — free-form annotation anchored to a click
 *   • measure    — measure Δprice + Δtime between two clicks (transient)
 *   • undo/redo  — history-based; redo cleared on new draw
 *
 * Persistence: drawings are stored per-symbol/timeframe in localStorage
 * so they survive reloads without needing a backend collection yet.
 */
const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
const FIB_COLORS = ['#94a3b8', '#00e5ff', '#00e5ff', '#f7c948', '#ff7ac6', '#ff7ac6', '#94a3b8'];

const LS_KEY = (sym, tf) => `atde:crypto:drawings:${sym}:${tf}`;

export default function DrawingOverlay({ engine, symbol, timeframe }) {
  const overlayRef = useRef(null);
  const [tool, setTool] = useState(null);   // null | 'horizontal' | 'trend' | 'fib' | 'text' | 'measure'
  const [pending, setPending] = useState(null);   // partial drawing being placed
  const [drawings, setDrawings] = useState([]);   // committed drawings
  const [redoStack, setRedoStack] = useState([]);
  const [hover, setHover] = useState(null);       // {x,y,time,price} cursor coords
  const [textInput, setTextInput] = useState({ open: false, x: 0, y: 0, time: 0, price: 0 });

  // ── Load / save drawings per-symbol/timeframe ──
  useEffect(() => {
    if (!symbol || !timeframe) return;
    try {
      const raw = localStorage.getItem(LS_KEY(symbol, timeframe));
      setDrawings(raw ? JSON.parse(raw) : []);
      setRedoStack([]);
    } catch { setDrawings([]); }
  }, [symbol, timeframe]);

  useEffect(() => {
    if (!symbol || !timeframe) return;
    try { localStorage.setItem(LS_KEY(symbol, timeframe), JSON.stringify(drawings)); }
    catch { /* quota */ }
  }, [drawings, symbol, timeframe]);

  // ── Chart coord translators ──
  const toCoord = useCallback((time, price) => {
    if (!engine) return null;
    const provider = engine.provider;
    if (!provider?.chart || !provider?.priceSeries) return null;
    const x = provider.chart.timeScale().timeToCoordinate(time);
    const y = provider.priceSeries.priceToCoordinate(price);
    return (x == null || y == null) ? null : { x, y };
  }, [engine]);

  const fromCoord = useCallback((x, y) => {
    if (!engine) return null;
    const provider = engine.provider;
    if (!provider?.chart || !provider?.priceSeries) return null;
    const time = provider.chart.timeScale().coordinateToTime(x);
    const price = provider.priceSeries.coordinateToPrice(y);
    return (time == null || price == null) ? null : { time, price };
  }, [engine]);

  // ── Mouse handlers ──
  const onMouseMove = (e) => {
    const rect = overlayRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const p = fromCoord(x, y);
    if (p) setHover({ x, y, ...p });
  };

  const onClick = (e) => {
    if (!tool || !hover) return;
    e.stopPropagation();
    if (tool === 'horizontal') {
      commit({ kind: 'horizontal', price: hover.price });
      return;
    }
    if (tool === 'text') {
      setTextInput({ open: true, x: hover.x, y: hover.y, time: hover.time, price: hover.price });
      return;
    }
    // trend / fib / measure — two-click tools
    if (!pending) {
      setPending({ kind: tool, a: { time: hover.time, price: hover.price } });
    } else {
      const b = { time: hover.time, price: hover.price };
      if (tool === 'measure') {
        commit({ kind: 'measure', a: pending.a, b, transient: true });
      } else {
        commit({ kind: tool, a: pending.a, b });
      }
      setPending(null);
    }
  };

  const commit = (drawing) => {
    setDrawings((d) => [...d, { ...drawing, id: `d_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` }]);
    setRedoStack([]);
  };

  const undo = () => {
    setDrawings((d) => {
      if (!d.length) return d;
      const [last, ...rest] = [...d].reverse();
      setRedoStack((r) => [...r, last]);
      return d.slice(0, -1);
    });
  };
  const redo = () => {
    setRedoStack((r) => {
      if (!r.length) return r;
      const [last, ...rest] = [...r].reverse();
      setDrawings((d) => [...d, last]);
      return r.slice(0, -1);
    });
  };
  const clearAll = () => { setDrawings([]); setRedoStack([]); };

  // ── Text input popover ──
  const submitText = (val) => {
    if (val.trim()) {
      commit({ kind: 'text', time: textInput.time, price: textInput.price, text: val.trim() });
    }
    setTextInput({ open: false, x: 0, y: 0, time: 0, price: 0 });
  };

  // ── Canvas draw ──
  useEffect(() => {
    const cnv = overlayRef.current?.querySelector('canvas');
    if (!cnv || !overlayRef.current) return;
    const rect = overlayRef.current.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    cnv.width = rect.width * dpr;
    cnv.height = rect.height * dpr;
    cnv.style.width = `${rect.width}px`;
    cnv.style.height = `${rect.height}px`;
    const ctx = cnv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.font = '11px "JetBrains Mono", monospace';

    // draw a single committed drawing
    const drawItem = (d) => {
      ctx.lineWidth = 1;
      if (d.kind === 'horizontal') {
        const c = toCoord(hover?.time ?? 0, d.price);
        if (!c) return;
        ctx.strokeStyle = '#f7c948'; ctx.setLineDash([6, 4]);
        ctx.beginPath(); ctx.moveTo(0, c.y); ctx.lineTo(rect.width, c.y); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#f7c948';
        ctx.fillText(`$${d.price.toLocaleString(undefined, { maximumFractionDigits: 4 })}`, rect.width - 90, c.y - 4);
      }
      if (d.kind === 'trend') {
        const a = toCoord(d.a.time, d.a.price);
        const b = toCoord(d.b.time, d.b.price);
        if (!a || !b) return;
        ctx.strokeStyle = '#00e5ff'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
        for (const p of [a, b]) {
          ctx.fillStyle = '#00e5ff';
          ctx.beginPath(); ctx.arc(p.x, p.y, 3, 0, Math.PI * 2); ctx.fill();
        }
      }
      if (d.kind === 'fib') {
        const a = toCoord(d.a.time, d.a.price);
        const b = toCoord(d.b.time, d.b.price);
        if (!a || !b) return;
        const highP = Math.max(d.a.price, d.b.price);
        const lowP  = Math.min(d.a.price, d.b.price);
        const range = highP - lowP;
        const xL = Math.min(a.x, b.x);
        const xR = Math.max(a.x, b.x);
        FIB_LEVELS.forEach((lvl, idx) => {
          const price = highP - range * lvl;
          const c = toCoord(d.a.time, price);
          if (!c) return;
          ctx.strokeStyle = FIB_COLORS[idx]; ctx.globalAlpha = 0.75; ctx.setLineDash([3, 3]);
          ctx.beginPath(); ctx.moveTo(xL, c.y); ctx.lineTo(xR + 60, c.y); ctx.stroke();
          ctx.setLineDash([]); ctx.globalAlpha = 1;
          ctx.fillStyle = FIB_COLORS[idx];
          ctx.fillText(`${(lvl * 100).toFixed(1)}% · $${price.toFixed(2)}`, xR + 4, c.y - 2);
        });
      }
      if (d.kind === 'text') {
        const c = toCoord(d.time, d.price);
        if (!c) return;
        ctx.fillStyle = '#e2e8f0';
        // pill background
        const padX = 6; const padY = 3;
        const tw = ctx.measureText(d.text).width;
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(c.x - padX, c.y - 12 - padY, tw + padX * 2, 12 + padY * 2);
        ctx.strokeStyle = 'rgba(0,229,255,0.5)';
        ctx.strokeRect(c.x - padX, c.y - 12 - padY, tw + padX * 2, 12 + padY * 2);
        ctx.fillStyle = '#e2e8f0';
        ctx.fillText(d.text, c.x, c.y - 3);
      }
      if (d.kind === 'measure') {
        const a = toCoord(d.a.time, d.a.price);
        const b = toCoord(d.b.time, d.b.price);
        if (!a || !b) return;
        const dPrice = d.b.price - d.a.price;
        const dPct = d.a.price ? (dPrice / d.a.price) * 100 : 0;
        const dTime = Math.abs(d.b.time - d.a.time);
        const color = dPct >= 0 ? '#00ff88' : '#ff3366';
        // rect
        ctx.fillStyle = dPct >= 0 ? 'rgba(0,255,136,0.10)' : 'rgba(255,51,102,0.10)';
        ctx.fillRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
        ctx.strokeStyle = color;
        ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
        // label
        const lbl = `${dPct >= 0 ? '+' : ''}${dPct.toFixed(2)}%  Δ$${dPrice.toFixed(2)}  ${(dTime / 3600).toFixed(1)}h`;
        ctx.fillStyle = color;
        ctx.fillText(lbl, Math.min(a.x, b.x) + 4, Math.min(a.y, b.y) + 12);
      }
    };

    drawings.forEach(drawItem);

    // pending / preview
    if (pending && hover) {
      const a = toCoord(pending.a.time, pending.a.price);
      if (a) {
        ctx.strokeStyle = 'rgba(0,229,255,0.7)'; ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(hover.x, hover.y); ctx.stroke();
        ctx.setLineDash([]);
      }
    }
  }, [drawings, pending, hover, toCoord]);

  // ── Redraw on chart pan/zoom ──
  useEffect(() => {
    if (!engine?.provider?.chart) return;
    const chart = engine.provider.chart;
    const rerender = () => setDrawings((d) => [...d]); // force re-draw
    // lightweight-charts fires subscribeCrosshairMove on any interaction
    chart.timeScale().subscribeVisibleTimeRangeChange(rerender);
    return () => { try { chart.timeScale().unsubscribeVisibleTimeRangeChange(rerender); } catch { /* noop */ } };
  }, [engine]);

  const tools = [
    { k: 'horizontal', label: 'H-LINE',  icon: Minus,       tip: 'Horizontal line' },
    { k: 'trend',      label: 'TREND',   icon: TrendingUp,  tip: 'Trend line — 2 clicks' },
    { k: 'fib',        label: 'FIB',     icon: TrendingUp,  tip: 'Fib retracement — 2 clicks' },
    { k: 'text',       label: 'TEXT',    icon: TypeIcon,    tip: 'Text note' },
    { k: 'measure',    label: 'MEASURE', icon: Ruler,       tip: 'Measure Δprice/time' },
  ];

  return (
    <>
      {/* Vertical toolbar overlaid on the left of the chart */}
      <div
        className="absolute top-2 left-2 z-30 flex flex-col gap-1 bg-black/70 border border-[var(--border-default)] backdrop-blur-sm p-1"
        data-testid="crypto-draw-toolbar"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {tools.map((t) => {
          const Icon = t.icon;
          const active = tool === t.k;
          return (
            <button
              key={t.k}
              onClick={() => { setTool(active ? null : t.k); setPending(null); }}
              className={`p-1.5 border ${active ? 'border-cyan-sys bg-cyan-sys text-black' : 'border-transparent text-[var(--txt-secondary)] hover:border-cyan-sys hover:text-cyan-sys'}`}
              title={t.tip}
              data-testid={`crypto-draw-${t.k}`}
            >
              <Icon size={12} />
            </button>
          );
        })}
        <div className="h-px w-full bg-[var(--border-default)] my-0.5" />
        <button onClick={undo} disabled={!drawings.length}
                className="p-1.5 border border-transparent hover:border-hold text-[var(--txt-secondary)] hover:text-hold disabled:opacity-30"
                title="Undo" data-testid="crypto-draw-undo">
          <Undo2 size={12} />
        </button>
        <button onClick={redo} disabled={!redoStack.length}
                className="p-1.5 border border-transparent hover:border-hold text-[var(--txt-secondary)] hover:text-hold disabled:opacity-30"
                title="Redo" data-testid="crypto-draw-redo">
          <Redo2 size={12} />
        </button>
        <button onClick={clearAll} disabled={!drawings.length}
                className="p-1.5 border border-transparent hover:border-sell text-[var(--txt-secondary)] hover:text-sell disabled:opacity-30"
                title="Clear all" data-testid="crypto-draw-clear">
          <Trash2 size={12} />
        </button>
      </div>

      {/* Full-canvas overlay for pointer + drawing */}
      <div
        ref={overlayRef}
        onMouseMove={onMouseMove}
        onClick={onClick}
        className={`absolute inset-0 z-20 ${tool ? 'cursor-crosshair' : 'pointer-events-none'}`}
        data-testid="crypto-draw-overlay"
        style={{ pointerEvents: tool ? 'auto' : 'none' }}
      >
        <canvas className="w-full h-full block" />
      </div>

      {/* Text input popover */}
      {textInput.open && (
        <div
          className="absolute z-40 bg-black border border-cyan-sys p-1"
          style={{ left: textInput.x + 8, top: textInput.y - 24 }}
        >
          <input
            autoFocus
            data-testid="crypto-draw-text-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submitText(e.currentTarget.value); }
              if (e.key === 'Escape') setTextInput({ open: false, x: 0, y: 0, time: 0, price: 0 });
            }}
            className="bg-black border border-[var(--border-default)] text-[11px] font-mono text-cyan-sys outline-none px-1 py-0.5"
            placeholder="annotation…"
          />
        </div>
      )}
    </>
  );
}
